import { isEmpty, isNil, isObject } from 'lodash'
import compose from 'lodash/fp/compose'
import prop from 'lodash/fp/prop'
import { updateInstanceWorkingTableCount } from 'src/App/actions'
import {
  documentsStore,
  errorsStore,
  linkedDocumentsStore,
  synapse
} from 'services/state'
import { getDocumentUrl, patchDocumentFetch } from 'services/api/documents'
import {
  documentWizardSave,
  documentWizardUpdate,
  duplicateDocuments,
  fetchDocuments,
  getDetailDocumentOptionsForFilter,
  getDocumentOptionsForFilter,
  removePaginatedDocuments
} from 'services/api/workingTables'
import {
  extraKeys,
  stagingKeys
} from 'utils/documents/DocumentMapper/constants'
import { buildRowId, nullFieldsByState } from 'utils/documents'
import { getDefaultValues } from 'utils/documents/default-values'
import { validateFields } from 'utils/validation'
import { closeAllModals, openModal } from 'components/Modal/actions'
import { tableDataMode } from './constants'
import dispatcher from './eventsDispatcher'
import { buildBodyWithSelection } from './helpers/data'
import {
  saveDocument,
  processDocument,
  getEitherDocumentOrValidationErrors,
  displayValidationErrorsIfAny,
  getEitherDocumentsOrAllValidationErrors,
  displayAllValidationErrorsIfAny,
  processAllDocuments,
  saveAllDocuments,
  processPatchDocument,
  savePatchDocument,
  getStagingDocumentOrValidationErrors,
  saveAllStagingDocuments,
  processAllStagingDocuments
} from './helpers/saving'
import { initialDocumentState, initialLinkedDocumentsState } from './state'
import { selectAllEditedDocuments } from './selectors'
import { addNotification } from 'components/Notifications/actions'
import { saveAutomatedPanelInfo } from './helpers/saveAutomatedPanel'

const { DOCUMENT_ID_KEY, IS_BEING_EDITED } = extraKeys

const clientSideDetailsSaving = [
  saveDocument,
  processDocument,
  displayValidationErrorsIfAny,
  getEitherDocumentOrValidationErrors
]

const serverSideDetailsSaving = [
  savePatchDocument,
  processPatchDocument,
  displayValidationErrorsIfAny,
  getStagingDocumentOrValidationErrors
]

const clientSideDetailsSavingAll = [
  saveAllDocuments,
  processAllDocuments,
  displayAllValidationErrorsIfAny,
  getEitherDocumentsOrAllValidationErrors
]

const serverSideDetailsSavingAll = [
  saveAllStagingDocuments,
  processAllStagingDocuments,
  displayAllValidationErrorsIfAny,
  getEitherDocumentsOrAllValidationErrors
]

export const getDocuments = fetchDocuments

export const resetSelectionStore = () => {
  documentsStore.update((prevState) => ({
    ...prevState,
    selection: initialDocumentState.selection
  }))
}

export const resetEditedModelInfoStore = () => {
  documentsStore.update((prevState) => ({
    ...prevState,
    editedModelInfo: initialDocumentState.editedModelInfo
  }))
}

export const resetDocumentsStore = () => {
  documentsStore.set(initialDocumentState)
}

export const resetLinkedDocumentsStore = () => {
  linkedDocumentsStore.set(initialLinkedDocumentsState)
}

export const refreshDocuments = () => {
  dispatcher.refreshEvent({ overwrite: true })
  updateInstanceWorkingTableCount()
}

export const purgeWorkingTableDocuments = (documentType) => {
  documentsStore.update((prevState) => {
    const documentsById = {}
    const editedDocuments = []

    for (const [key, document] of Object.entries(prevState.documentsById)) {
      // Only keep data on documents that are of a different type
      if (document.type !== documentType) {
        documentsById[key] = document
        editedDocuments.push(key)
      }
    }

    return { ...prevState, documentsById, editedDocuments }
  })
}

export const updateDocuments = ({
  documentId,
  field,
  value,
  rules,
  mapper,
  sectionMap
}) => {
  const { documentsById, latestEdit, editedDocuments, ...prevState } =
    documentsStore.get()

  const updatedDocument = {
    ...documentsById[documentId],
    [IS_BEING_EDITED]: true,
    [field]: value
  }

  const isNew = !updatedDocument.id.id

  const fieldStates = validateFields(updatedDocument, rules, {
    showValidationResult: false
  })

  mapper.frontend
    // At every update, virtual fields (readonly, calculated, ...) are calculated again
    .addVirtualFields(
      nullFieldsByState(updatedDocument, fieldStates, sectionMap)
    )
    .then((document) => {
      documentsStore.set({
        ...prevState,
        latestEdit: isNew ? latestEdit : documentId,
        editedDocuments:
          isNew || editedDocuments.includes(documentId)
            ? editedDocuments
            : [...editedDocuments, documentId],
        documentsById: { ...documentsById, [documentId]: document }
      })
      // Dispatch after the state update, so subscribers will get the latest values from the store
      // used also to reevaluate the visibility of sections
      synapse.publish('FieldChanged', {
        field,
        documentId
      })
    })
}

export const loadDocumentIntoStore = async (type, id, mapper) => {
  const documentURL = getDocumentUrl(type, id.activityId, id.id)
  const {
    body: { data: document }
  } = await synapse.fetch(documentURL)

  const mappedDocument = await mapper.frontend.transformDocument(document)
  const documentId = buildRowId(document.id)

  documentsStore.update((prevState) => {
    return {
      ...prevState,
      editedDocuments: prevState.editedDocuments.filter(
        (id) => id !== documentId
      ), // A newly fetched document should not be considered edited
      documentsById: {
        ...prevState.documentsById,
        [documentId]: mappedDocument
      }
    }
  })
}

export const createNewEmptyDocument = async (
  temporaryId,
  {
    mainSection,
    detailItemIds,
    workingTableId,
    permissions,
    defaultValueGetter,
    scenario,
    instanceId,
    mapper
  }
) => {
  const defaultValues = await getDefaultValues(mainSection.fields, {
    defaultValueGetter,
    scenario,
    instanceId
  })
  const newDocument = {
    ...defaultValues,
    ...detailItemIds.reduce((acc, { id }) => ({ ...acc, [id]: [] }), {}), // Initialize details with an empty list
    ...permissions,
    id: { activityId: workingTableId },
    [DOCUMENT_ID_KEY]: temporaryId
  }

  // We also need to get virtual fields (readonly, calculated, ...) that may be dependent on default values
  const finalNewDocument = !isEmpty(defaultValues)
    ? await mapper.frontend.addVirtualFields(newDocument)
    : newDocument

  documentsStore.update((prevState) => ({
    ...prevState,
    documentsById: {
      ...prevState.documentsById,
      [temporaryId]: finalNewDocument
    }
  }))
}

const composeWithParams = (composition, params) =>
  compose(...composition.map((fn) => fn(params)))

/**
 * Check document validity, displaying a notification if there are errors; if not, process the document and save it.
 * @param {string} documentId
 * @param {Object} params
 * @returns {Promise} The saved document | null
 */
export const startDocumentSavingProcess = (documentId, params) => {
  const isSomeDocumentLazy = params.detailItemIds.some(({ lazy }) => lazy)
  const document = documentsStore.get(
    ({ documentsById }) => documentsById[documentId]
  )

  const functionsComposition =
    isSomeDocumentLazy && !params.isNew
      ? serverSideDetailsSaving
      : clientSideDetailsSaving

  const composition = composeWithParams(functionsComposition, params)

  return composition(document)
}

/**
 * Save multiple documents, only if all of them are valid; documents must be of the same type.
 * @param {Object} params
 * @returns {Promise} The saved documents | null
 */
export const startMultipleDocumentsSavingProcess = (params) => {
  const { detailItemIds, documentType } = params

  const editedDocuments = documentsStore.get(selectAllEditedDocuments)
  const isSomeDocumentLazy = detailItemIds.some(({ lazy }) => lazy)

  const functionsComposition = isSomeDocumentLazy
    ? serverSideDetailsSavingAll
    : clientSideDetailsSavingAll

  const composition = composeWithParams(functionsComposition, params)
  const sameTypeDocuments = editedDocuments.filter(
    ({ type }) => type === documentType
  )

  return composition(sameTypeDocuments)
}

export const getRequestFullBody = ({ api, columnApi, selection }) => {
  const filterModel = api.getFilterModel()
  const sortModel = api.getSortModel()
  const start = api.paginationGetCurrentPage() * 10

  const { body } = buildBodyWithSelection({
    selection,
    filterModel,
    sortModel,
    start,
    columnApi
  })
  return body
}

export const handleDeletionError = ({ responseBody }) => {
  const { documents } = JSON.parse(responseBody.debugMessage)
  closeAllModals()
  openModal('documentDeletionErrors', { deletionErrors: documents })
  // TODO: in be response internal status code must be set to a specific error name
  // (es: not INTERNAL SERVER ERROR but DELETE NOT ALLOWED)
  // If the name of the error will be clearer, in the synapse error func we can filter the error in the store
  // clarify with BE the error code
  errorsStore.update(() => []) // This line is to hide 403 error msg, because we already have the notification
  throw new Error(responseBody) // Rethrow to properly work with modals
}

export const deleteCurrentlySelectedDocuments = async ({
  tableMode,
  gridApi,
  workingTableId,
  workingTableType,
  instanceId,
  t
}) => {
  const selection = documentsStore.get((store) => store.selection)
  const body = getRequestFullBody({ ...gridApi, selection })

  if (tableMode === tableDataMode.CLIENT_SIDE) {
    body.filter = {}
  }

  saveAutomatedPanelInfo('DELETE', {
    wt: workingTableId,
    documentType: workingTableType
  })

  addNotification({ message: t('DeleteStarted') })
  setTimeout(() => {
    addNotification({
      message: t('DeletePending')
    })
  }, 500)

  try {
    // chiudiamo tutte le modali prima di lanciare la delete
    closeAllModals()
    const { body: deleted } = await removePaginatedDocuments(
      workingTableId,
      workingTableType,
      body
    )

    closeTabsAndDispatchUniqueCrudEvents(deleted, instanceId)
    gridApi.api.deselectAll()
    resetSelectionStore()
    updateInstanceWorkingTableCount()
    dispatcher.refreshEvent({ overwrite: false })
  } catch ({ responseBody, code }) {
    // closeAllModals()
    if (code === 400) {
      errorsStore.update(() => [])
    }

    throw new Error(responseBody)
  }
}

// Options used for filtering
export function getOptionsFromWTDocument(
  wtID,
  { detailField, documentId, field }
) {
  const effect =
    documentId && detailField
      ? getDetailDocumentOptionsForFilter // Server side detail table
      : getDocumentOptionsForFilter // Server side master table

  return effect(wtID, { documentId, detailField, field }).then(prop('body'))
}

export const closeTabsAndDispatchUniqueCrudEvents = (documents, instanceId) => {
  documents.forEach(({ id, temporaryId }) => {
    dispatcher.documentTabClosed(id || temporaryId)
  })

  const uniqueDocumentTypes = documents.reduce(
    (acc, { type }) => (acc.includes(type) ? acc : [...acc, type]),
    []
  )

  // Since it only interacts with the options cache, it is better to dispatch a single CRUD event for document type
  uniqueDocumentTypes.forEach((type) => {
    dispatcher.crudEvent({ type, instanceId })
  })
}

export const duplicateCurrentlySelectedDocuments = async ({
  gridApi,
  workingTableId
}) => {
  const selection = documentsStore.get((store) => store.selection)
  const body = getRequestFullBody({ ...gridApi, selection })

  try {
    await duplicateDocuments(workingTableId, body)
    gridApi.api.deselectAll()
    resetSelectionStore()
    dispatcher.refreshEvent({ overwrite: true })
  } catch (error) {
    console.error(error)
  }
}

export const deleteServerSideDetails = async ({
  path,
  selection,
  gridApi,
  documentObjectId
}) => {
  const body = getRequestFullBody({ ...gridApi, selection })

  return patchDocumentFetch({
    data: [
      {
        op: stagingKeys.DELETE,
        path,
        filter: { selection: body }
      }
    ],
    id: documentObjectId
  }).then(() => {
    dispatcher.documentRefreshed(documentObjectId)
  })
}

export const duplicateServerSideDetails = async ({ gridApi }) => {}

/**
 * Request the document to be loaded into the new document tab
 * If there is not a document to show in a new tab, it performs only a refresh
 * @returns If the tab should be opened or not after the request
 */

export const requestWizardDocument = async ({
  activityId,
  body,
  temporaryId,
  mapper
}) => {
  const documentBody = await documentWizardSave({
    activityId,
    body
  })

  if (isEmpty(documentBody)) {
    // refresh inputview
    dispatcher.refreshEvent({ overwrite: false })
    return false
  }

  const document = {
    ...documentBody,
    id: { activityId: activityId, id: temporaryId }
  }
  const mappedDocument = await mapper.frontend.transformDocument(
    document,
    temporaryId,
    activityId
  )

  const sDocument = {
    ...mappedDocument,
    id: { activityId }
  }

  cleanUpDocument(sDocument)
  documentsStore.update((prevState) => ({
    ...prevState,
    documentsById: {
      ...prevState.documentsById,
      [sDocument._documentId]: sDocument
    }
  }))
  return true
}

const cleanUpDocument = (obj) => {
  if (isEmpty(obj)) return
  for (const key in obj) {
    const value = obj[key]
    if (isObject(value)) {
      cleanUpDocument(value)
    } else if (isNil(value)) {
      delete obj[key]
    }
  }
}

export const requestWizardDocumentUpdate = async ({ activityId, body }) => {
  await documentWizardUpdate({
    activityId,
    body
  })

  // refresh inputview
  dispatcher.refreshEvent({ overwrite: false })

  return true
}
